import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';

/**
 * Marking a maintenance entry done — ONE action, not one per surface.
 *
 * The register's two due tables and the asset page's own calendar all offer it, and each
 * had grown its own `useMutation`, its own error notice and its own disabled rule. Two
 * spellings of one action is how a codebase ends up with fifteen cards and five idioms —
 * and the notice copy had already drifted between the two by the time it was caught.
 *
 * Its own file rather than a corner of `assetVocab.ts`: that module is deliberately pure
 * words and copy rules, which is what lets a guard say plainly that nothing in it may
 * construct a Date.
 */
export function useCompleteMaintenance() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const complete = useMutation(
    trpc.asset.maintenanceComplete.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  return {
    /**
     * The write failed and the row is still on screen saying it is owed — which is true,
     * and the reason every caller renders this in place rather than in a toast that
     * takes the only account of the failure away with it.
     */
    error: complete.error,
    pending: complete.isPending,
    markDone: (maintenanceId: string) => complete.mutate({ maintenanceId, ...idem() }),
  };
}
